// src/lib/svg/textToPath.js Convert text objects to SVG path data using opentype.js. Requires font files — uses bundled open-source metric-compatible fonts that map to common web-safe font names.
import opentype from 'opentype.js'
import { resetPathOrigin, stringToRawPath } from './paths'
import { rawPathBounds } from '~/lib/objectBounds'

const fontCache = new Map()

const CDN = 'https://cdn.jsdelivr.net/gh/ArtifexSoftware/urw-base35-fonts@master/fonts'

// Map web-safe font names to URW Base 35 open-source equivalents with bold/italic variants. OFL-licensed fonts safe to fetch.
const FONT_URLS = {
	Arial: {
		regular: `${CDN}/NimbusSans-Regular.otf`,
		bold: `${CDN}/NimbusSans-Bold.otf`,
		italic: `${CDN}/NimbusSans-Italic.otf`,
		boldItalic: `${CDN}/NimbusSans-BoldItalic.otf`,
	},
	Helvetica: {
		regular: `${CDN}/NimbusSans-Regular.otf`,
		bold: `${CDN}/NimbusSans-Bold.otf`,
		italic: `${CDN}/NimbusSans-Italic.otf`,
		boldItalic: `${CDN}/NimbusSans-BoldItalic.otf`,
	},
	Georgia: {
		regular: `${CDN}/C059-Roman.otf`,
		bold: `${CDN}/C059-Bold.otf`,
		italic: `${CDN}/C059-Italic.otf`,
		boldItalic: `${CDN}/C059-BdIta.otf`,
	},
	'Times New Roman': {
		regular: `${CDN}/NimbusRoman-Regular.otf`,
		bold: `${CDN}/NimbusRoman-Bold.otf`,
		italic: `${CDN}/NimbusRoman-Italic.otf`,
		boldItalic: `${CDN}/NimbusRoman-BoldItalic.otf`,
	},
	'Courier New': {
		regular: `${CDN}/NimbusMonoPS-Regular.otf`,
		bold: `${CDN}/NimbusMonoPS-Bold.otf`,
		italic: `${CDN}/NimbusMonoPS-Italic.otf`,
		boldItalic: `${CDN}/NimbusMonoPS-BoldItalic.otf`,
	},
	Verdana: {
		regular: `${CDN}/NimbusSans-Regular.otf`,
		bold: `${CDN}/NimbusSans-Bold.otf`,
		italic: `${CDN}/NimbusSans-Italic.otf`,
		boldItalic: `${CDN}/NimbusSans-BoldItalic.otf`,
	},
	Impact: {
		regular: `${CDN}/NimbusSans-Bold.otf`,
		bold: `${CDN}/NimbusSans-Bold.otf`,
		italic: `${CDN}/NimbusSans-BoldItalic.otf`,
		boldItalic: `${CDN}/NimbusSans-BoldItalic.otf`,
	},
	'Trebuchet MS': {
		regular: `${CDN}/NimbusSans-Regular.otf`,
		bold: `${CDN}/NimbusSans-Bold.otf`,
		italic: `${CDN}/NimbusSans-Italic.otf`,
		boldItalic: `${CDN}/NimbusSans-BoldItalic.otf`,
	},
}

const DEFAULT_FONTS = FONT_URLS['Arial']

/**
 * Load and cache an opentype.js font.
 * @param {string} fontFamily
 * @param {boolean} bold
 * @param {boolean} italic
 * @returns {Promise<opentype.Font>}
 */
async function loadFont(fontFamily, bold, italic) {
	const variant = bold && italic ? 'boldItalic' : bold ? 'bold' : italic ? 'italic' : 'regular'
	const cacheKey = `${fontFamily}-${variant}`
	if (fontCache.has(cacheKey)) return fontCache.get(cacheKey)

	const familyUrls = FONT_URLS[fontFamily] || DEFAULT_FONTS
	const url = familyUrls[variant] || familyUrls.regular
	let font
	try {
		font = await opentype.load(url)
	} catch (err) {
		throw new Error(`Failed to load font "${fontFamily}" (${variant}): ${err.message}`)
	}
	fontCache.set(cacheKey, font)
	return font
}

/**
 * Compute the advance width of a line using opentype glyph metrics.
 */
function measureLineAdvance(font, line, fontSize, letterSpacing) {
	let width = 0
	for (let j = 0; j < line.length; j++) {
		const glyph = font.charToGlyph(line[j])
		const advance = glyph?.advanceWidth ? (glyph.advanceWidth / font.unitsPerEm) * fontSize : fontSize * 0.5
		width += advance
		if (j < line.length - 1) width += letterSpacing
	}
	return width
}

/**
 * Convert a text display object to SVG path data. Returns { data, x, y, width, height } suitable for a 'path' object.
 *
 * @param {object} obj - Text display object from Redux
 * @returns {Promise<{ data: string, x: number, y: number, width: number, height: number }>}
 */
export async function expandTextToPath(obj) {
	const {
		text = '',
		fontFamily = 'Arial',
		fontSize = 24,
		bold = false,
		italic = false,
		textScaleX = 1,
		textAlign = 'left',
		letterSpacing = 0,
		lineHeight = 1.2,
		x = 0,
		y = 0,
	} = obj

	if (!text.trim()) {
		throw new Error('Cannot expand empty text to path')
	}

	const font = await loadFont(fontFamily, bold, italic)
	const lines = text.split('\n')
	const lineHeightPx = lineHeight * fontSize
	const paths = []

	// Pre-compute line widths for textAlign
	const lineWidths = lines.map(line => measureLineAdvance(font, line, fontSize, letterSpacing))
	const maxLineWidth = Math.max(...lineWidths)

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i]
		if (!line) continue

		const yOffset = i * lineHeightPx

		// textAlign offset
		let xStart = 0
		if (textAlign === 'center') {
			xStart = (maxLineWidth - lineWidths[i]) / 2
		} else if (textAlign === 'right') {
			xStart = maxLineWidth - lineWidths[i]
		}

		// Build path for each character with letter spacing
		let xCursor = xStart
		for (let j = 0; j < line.length; j++) {
			const char = line[j]
			const glyph = font.charToGlyph(char)
			if (!glyph) continue

			const glyphPath = glyph.getPath(xCursor, yOffset + fontSize, fontSize)
			// Apply textScaleX — scale all x coordinates
			if (textScaleX !== 1) {
				for (const cmd of glyphPath.commands) {
					if (cmd.x !== undefined) cmd.x *= textScaleX
					if (cmd.x1 !== undefined) cmd.x1 *= textScaleX
					if (cmd.x2 !== undefined) cmd.x2 *= textScaleX
				}
			}
			const pathData = glyphPath.toPathData(2)
			if (pathData && pathData !== 'M0 0') {
				paths.push(pathData)
			}

			// Advance cursor
			const advance = glyph.advanceWidth ? (glyph.advanceWidth / font.unitsPerEm) * fontSize : fontSize * 0.5
			xCursor += advance + letterSpacing
		}
	}

	if (paths.length === 0) {
		throw new Error('Text produced no visible glyphs')
	}

	// Combine all glyph paths into one SVG path string
	const combinedPath = paths.join(' ')

	// Reset origin so the path starts at (0,0) in local space
	const result = resetPathOrigin(combinedPath)

	// Compute bounds from the normalized path data
	const rawPath = stringToRawPath(result.data)
	const bounds = rawPathBounds(rawPath)

	return {
		data: result.data,
		x: x + result.x,
		y: y + result.y,
		width: bounds.width,
		height: bounds.height,
	}
}
